import { useCallback, useEffect, useRef } from "react";

import { Button, isTouchDevice, TextArea } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { useForm } from "react-hook-form";

import { type Comment, type ModelCommentV2, useMutateComments } from "../../hooks/useComments";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { useWebExport } from "../../hooks/useWebExport";
import { CommentContextMenu } from "./CommentContextMenu";
import { CommentHeader } from "./CommentHeader";
import { NewCommentHeader } from "./NewCommentHeader";

import style from "./Comment.module.css";

interface CommentPropsBase {
  title: string;
  isNewerVersion?: boolean;
}

interface CommentPropsView extends CommentPropsBase {
  initialMode: "view";
  date: string;
  isTruncated: boolean;
  children: React.ReactNode;
  actions?: React.ReactNode;
}

interface CommentPropsNew extends CommentPropsBase {
  initialMode: "new";
  dispose: (params: { newComment: Comment | null }) => void;
  position: ModelCommentV2;
  actions?: React.ReactNode;
}

type CommentProps = CommentPropsView | CommentPropsNew;

type CommentFormData = {
  comment: string;
};

const CommentComponent = (props: CommentProps) => {
  if (props.initialMode === "new") {
    return <NewComment {...props} />;
  }

  return <ViewComment {...props} />;
};

const NewComment = ({ title, dispose, position, actions, isNewerVersion }: CommentPropsNew) => {
  const tracker = useTracker();
  const { data: webExportData, viewLinkId } = useWebExport();
  const { t, m } = useTranslation();

  const { mutateAsync, isPending, isError } = useMutateComments();

  const {
    register,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm<CommentFormData>();

  const submit = useCallback(
    async (data: CommentFormData) => {
      try {
        const newComment = await mutateAsync({
          body: {
            message: data.comment,
            model: {
              ...position,
            },
          },
        });

        viewLinkId &&
          tracker.trackCommentAdded({
            commentId: newComment.commentId,
            type: "model",
            viewLinkId,
            webExportId: webExportData?.webExport.webExportId,
          });

        reset();
        dispose({ newComment: newComment ?? null });
      } catch {
        // This is to not throw Unhandled Promise Rejection error
      }
    },
    [position, mutateAsync, reset, dispose, tracker, viewLinkId, webExportData]
  );

  const textAreaElementRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (textAreaElementRef.current) {
      textAreaElementRef.current.focus();

      // Scroll to top on touch devices to avoid keyboard covering the textarea on iOS
      if (isTouchDevice()) {
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 200);
      }
    }
  }, [textAreaElementRef]);

  const { ref, ...rest } = register("comment", { required: true });

  return (
    <div className={classNames(style.commentWrapper, isNewerVersion && style.newerVersion)}>
      <form onSubmit={handleSubmit(submit)}>
        <NewCommentHeader title={title} actions={actions} />
        <div className={style.form}>
          <div className={style.commentRow}>
            <TextArea
              {...rest}
              ref={(e) => {
                ref(e);
                textAreaElementRef.current = e;
              }}
              maxLength={1000}
              rows={2}
              className={style.textArea}
              placeholder={t(m.Comments.AddCommentPlaceholder)}
              errorMessage={isError ? t(m.Comments.SubmitError) : undefined}
            />
          </div>
          <Button type="submit" className={style.button} disabled={!isValid || isPending}>
            {t(m.Comments.SendButton)}
          </Button>
        </div>
      </form>
    </div>
  );
};

const ViewComment = ({
  title,
  date,
  isTruncated,
  children,
  actions,
  isNewerVersion,
}: CommentPropsView) => {
  return (
    <div className={classNames(style.commentWrapper, isNewerVersion && style.newerVersion)}>
      <CommentHeader name={title} commentedAt={date} actions={actions} />
      <div className={classNames(style.text, isTruncated && style.textTruncated)}>{children}</div>
    </div>
  );
};

CommentComponent.ContextMenu = CommentContextMenu;

export { CommentComponent as Comment };
